
import { useEffect, useState, useCallback } from "react";

// Extend the Window interface to include ZohoDeskAsapReady
declare global {
    interface Window {
        ZohoDeskAsapReady: (cb: () => void) => void;
        ZohoDeskAsap__asyncalls?: (() => void)[];
        ZohoDeskAsapReadyStatus?: boolean;
    }
}

export default function ClientScripts() {

    console.log('ClientScripts from footer app');

    const [allowedPrefs, setAllowedPrefs] = useState<{ analytics: boolean, marketing: boolean, essential: boolean }>({ analytics: false, marketing: false, essential: false });

    const fetchPrefs = () => {
        try {
            // Check both localStorage keys for compatibility
            let stored = localStorage.getItem("cookiePreferences");
            if (!stored) {
                stored = localStorage.getItem("gfy_cookie_consent");
                if (stored) {
                    const consentData = JSON.parse(stored);
                    stored = JSON.stringify(consentData.preferences);
                }
            }
            
            if (stored) {
                const prefs = JSON.parse(stored);
                // Use the actual essential value from localStorage
                setAllowedPrefs({ ...prefs, essential: prefs.essential !== false });
            } else {
                // Set default with essential as false (no consent given)
                setAllowedPrefs({ analytics: false, marketing: false, essential: false });
            }
        } catch (err) {
            console.error("Cookie preference parsing error:", err);
            // Set default with essential as false (no consent given)
            setAllowedPrefs({ analytics: false, marketing: false, essential: false });
        }
    };

    useEffect(() => {
        fetchPrefs();

        const handler = () => fetchPrefs();
        
        // Listen for storage changes from other tabs
        window.addEventListener("storage", handler);
        
        // Listen for custom events from the same tab
        const customHandler = () => fetchPrefs();
        window.addEventListener("cookiePreferencesChanged", customHandler);
        
        return () => {
            window.removeEventListener("storage", handler);
            window.removeEventListener("cookiePreferencesChanged", customHandler);
        };
    }, []);

    const hasConsent = useCallback((type: 'analytics' | 'marketing' | 'essential'): boolean => {
        return allowedPrefs[type] === true;
    }, [allowedPrefs]);

    useEffect(() => {
        // Check if we're on production domain (gimmefy.ai only, not dev or staging subdomains)
        const isProd = window.location.host === 'gimmefy.ai';
        
        if (hasConsent("essential") && isProd) {
            // Google Tag Manager - only fire on production domain
            const script = document.createElement("script");
            script.innerHTML = `
        (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','GTM-KVNNW7F');
      `;
            document.head.appendChild(script);
        }

        if (hasConsent("analytics")) {
            // Zoho Desk
            const script = document.createElement("script");
            script.type = "text/javascript";
            script.id = "zohodeskasapscript";
            script.defer = true;
            script.nonce = "{place_your_nonce_value_here}";
            script.src = "https://desk.zoho.com/portal/api/web/asapApp/914615000002540085?orgId=833253071";
            document.head.appendChild(script);

            window.ZohoDeskAsapReady = function (cb: () => void) {
                const calls = window.ZohoDeskAsap__asyncalls || [];
                if (window.ZohoDeskAsapReadyStatus) {
                    if (cb) calls.push(cb);
                    calls.forEach((s: () => void) => s && s());
                    window.ZohoDeskAsap__asyncalls = null;
                } else {
                    if (cb) calls.push(cb);
                }
            };
        }

        if (hasConsent("marketing")) {
            // Sopro Outbase
            const script = document.createElement("script");
            script.innerHTML = `
        (function (o, u, t, b, a, s, e) {
          window[b] = window[b] || {};
          (e = e || [])['key'] = o;
          e['__obr'] = u.location.href;
          a = [];
          u.location.search.replace('?', '').split('&').forEach(function (q) {
            if (q.startsWith(b) || q.startsWith('_obid'))
              e[q.split('=')[0]] = q.split('=')[1];
          });
          e['_obid'] = e['_obid'] || (u.cookie.match(/(^|;)\\s*_obid\\s*=\\s*([^;]+)/) || []).pop() || 0;
          for (k in e) {
            if (e.hasOwnProperty(k))
              a.push(encodeURIComponent(k) + '=' + encodeURIComponent(e[k]));
          }
          s = u.createElement('script');
          s.src = t + '?' + a.join('&');
          u.body.appendChild(s);
        })('82d9ecc7-edb9-4dfd-9625-4a7f3430f51d', document, 'https://plugin.sopro.io/hq.js', 'outbase');
      `;
            document.body.appendChild(script);
        }
    }, [allowedPrefs, hasConsent]);

    return (
        <>
            {hasConsent("essential") && window.location.host === 'gimmefy.ai' && (
                <noscript>
                    <iframe
                        src="https://www.googletagmanager.com/ns.html?id=GTM-KVNNW7F"
                        height="0"
                        width="0"
                        style={{ display: "none", visibility: "hidden" }}
                    ></iframe>
                </noscript>
            )}
            {hasConsent("analytics") && (
                <noscript>
                    <div id="zoho-desk-noscript">
                        Zoho Desk support widget requires JavaScript to be enabled.
                    </div>
                </noscript>
            )}
        </>
    );
}
